/*
 * File: order.interface.ts
 * Project: meki
 * File Created: Wednesday, 21st October 2020 8:51:58 am
 * Author: Vicente Melin (vicente@inventures.cl)
 * -----
 * Last Modified: Monday, 19th June 2023 9:56:11 am
 * Modified By: Blanca Munizaga (blanca@inventures.cl)
 * -----
 * Copyright 2019 - 2020 Incrementa Ventures SpA. ALL RIGHTS RESERVED
 * Terms and conditions defined in license.txt
 * -----
 * Inventures - www.inventures.cl
 */

import { StoreObject } from '@apollo/client';
import { OrderDetail, Product } from '@interfaces';
import { Address } from './address.interface';
import { DevolutionOrder } from './devolutionOrder';
import { Payment } from './payment.interface';
import { PaymentMethod } from './paymentMethod.interface';
import { User } from './user.interface';
import { OrderInsurance } from '@queries/userInsurance/queries/getInsuranceQuote';
import { Discount } from './discount.interface';

export type DeliveryType = 'PICK_UP' | 'DELIVERY';

export interface DeliveryDateOptions {
  date: string;
}

export enum LocalVendorPaymentMethods {
  TRANSFER = 'TRANSFER',
  CARD_CASH = 'CARD_CASH',
  CARD = 'CARD',
  CASH = 'CASH',
}

export enum AssistantVendorPaymentMethods {
  TRANSFER = 'TRANSFER',
  CARD = 'CARD',
}

export interface LocalVendorOrderMetadata {
  paymentMethod: LocalVendorPaymentMethods;
  mixPayments: Record<string, number>;
}

export interface AssistantVendorOrderMetadata {
  paymentMethod: AssistantVendorPaymentMethods | null;
}

export interface UserInfoOrderMetadata {
  email: string;
  name: string;
  rut: string;
}

export interface OrderMetadata {
  fintocPaymentIntentId: string;
  cancelledByFintoc: boolean;
  payments: { type: string; amount: number }[];
  userInfo: UserInfoOrderMetadata;
  chargeDeliveryCost: boolean;
  useMekiCredits: boolean;
  insuranceInfo: InsuranceInfoMetadata;
  installments: number;
  localVendor: LocalVendorOrderMetadata;
  assistantVendor: AssistantVendorOrderMetadata;
  sendEmail: boolean;
  isFirstOrderReferrals: boolean;
}

export interface InsuranceInfoMetadata {
  insuranceId: number;
  claimId: number;
  totalReimbursement: number;
  totalDeductible: number;
  totalCopay: number;
  rut: string;
  reimbursedProducts: {
    sku: number;
    observation: string;
    unitReimbursement: number;
    unitCopay: number;
    unitDeductible: number;
    coverage: boolean;
    quantity: number;
  }[];
}
export interface Order extends StoreObject {
  id: number;
  uuid: string;
  quantity: number;
  status: string;
  comments: string;
  recieverName: string;
  dispatcherName: string;
  builderName: string;
  deliveryCost: number;
  createdAt: string;
  deliveryDate: string;
  minDeliveryDate: string;
  orderNumber: number;
  orderDetails: OrderDetail[];
  address: Address;
  paymentMethod: PaymentMethod;
  isMine: boolean;
  user?: User;
  deliveryType: DeliveryType;
  deliveryDateOptions: DeliveryDateOptions[];
  orderGroupId: string;
  totalCost: number;
  mekiCreditsUsed: number;
  onDemand: boolean;
  payments: Payment[];
  devolutionOrders: DevolutionOrder[];
  orderInsurance: OrderInsurance;
  receipts: {
    type: string;
    products: Product[];
    url: string;
    provider: string;
  }[];
  metadata: OrderMetadata;
  deliveryCoupon: boolean;
  discount: Discount;
}
